import React from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { Badge, Button, TableCell, TableRow } from '@roger/ui-components';
import QuickDiscontinueButton from './QuickDiscontinueButton';
import type { PatientMedicationResponse } from '../../services/api';
import {
  formatDate,
  getMedicationDisplayName,
  getMedicationDosageDisplay,
  isActuallyDiscontinued,
} from './medicationUtils';

interface MedicationTableRowProps {
  medication: PatientMedicationResponse;
  onEdit: (medication: PatientMedicationResponse) => void;
  onDelete: (medication: PatientMedicationResponse) => void;
  onDiscontinue: (medicationId: string, date: Date) => Promise<void>;
}

const MedicationTableRow: React.FC<MedicationTableRowProps> = ({
  medication: med,
  onEdit,
  onDelete,
  onDiscontinue,
}) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isFuture = new Date(med.start_date + 'T00:00:00') > today;
  const isDiscontinued = isActuallyDiscontinued(med);
  const isMuted = isFuture || isDiscontinued;

  const dosageDisplay = getMedicationDosageDisplay(med);

  return (
    <TableRow className={isMuted ? 'opacity-60' : undefined}>
      <TableCell>
        <div className='flex flex-col gap-1'>
          {isFuture ? (
            <Badge variant='secondary' className='text-xs w-fit'>
              Future
            </Badge>
          ) : isDiscontinued ? (
            <Badge variant='secondary' className='text-xs w-fit'>
              Discontinued
            </Badge>
          ) : (
            med.is_longstanding && (
              <Badge variant='outline' className='text-xs w-fit'>
                Longstanding
              </Badge>
            )
          )}
        </div>
      </TableCell>
      <TableCell className='text-sm'>{formatDate(med.start_date)}</TableCell>
      <TableCell className='text-sm'>
        {med.discontinue_date ? (
          <span className='text-muted-foreground'>
            {formatDate(med.discontinue_date)}
          </span>
        ) : (
          <QuickDiscontinueButton
            minDate={new Date(med.start_date + 'T00:00:00')}
            onDiscontinue={date => onDiscontinue(med.id, date)}
          />
        )}
      </TableCell>
      <TableCell>
        <div>
          <div className='font-medium'>{getMedicationDisplayName(med)}</div>
          {(dosageDisplay || med.frequency_instructions) && (
            <div className='text-sm text-muted-foreground mt-0.5'>
              {dosageDisplay}
              {dosageDisplay && med.frequency_instructions && ' - '}
              {med.frequency_instructions}
            </div>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className='flex items-center gap-1'>
          <Button variant='ghost' size='sm' onClick={() => onEdit(med)}>
            <Pencil className='h-4 w-4' />
          </Button>
          <Button variant='ghost' size='sm' onClick={() => onDelete(med)}>
            <Trash2 className='h-4 w-4 text-destructive' />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

export default MedicationTableRow;
